import { useEffect, useState } from "react";
import { Tooltip, Iconfont } from "@eweadn-qiankun/shared-components";
import { useTranslation } from "react-i18next";
import { getElementXyData } from "./hooks/useAdvancedKeyState";
import SettingTravelModal from "./SettingTravelModal";
import {
  HFD_MAGNETIC_DKS_DEFAULT_DBS,
  resolveMagneticDksDefaultDbs,
} from "./originalData";

/** 华奋达磁轴 DKS 四行程默认（与星闪无关） */
export const HFD_DKS_DEFAULT_DBS = HFD_MAGNETIC_DKS_DEFAULT_DBS;

const MAGNETIC_TIP_META: Array<{
  tipClass: string;
  labelKey: string;
  dbsIdx: 0 | 1 | 2 | 3;
}> = [
  {
    tipClass: "press",
    labelKey: "keyboard.advancedKey.dksModes.start",
    dbsIdx: 0,
  },
  {
    tipClass: "bottomOut",
    labelKey: "keyboard.advancedKey.dksModes.pressBottom",
    dbsIdx: 1,
  },
  {
    tipClass: "bottomOut",
    labelKey: "keyboard.advancedKey.dksModes.releaseBottom",
    dbsIdx: 2,
  },
  {
    tipClass: "reset",
    labelKey: "keyboard.advancedKey.dksModes.reset",
    dbsIdx: 3,
  },
];

const normalizeMagneticDbs = (
  dbs: unknown,
  keyboardType?: string | null
): number[] => {
  const src = Array.isArray(dbs) ? dbs.map((v) => Number(v)) : [];
  const defaults = [...resolveMagneticDksDefaultDbs(keyboardType)];
  if (src.length < 4) {
    return defaults;
  }
  return defaults.map((fallback, i) => {
    const n = src[i];
    return Number.isFinite(n) && n > 0 ? n : fallback;
  });
};

const dks = (props: any) => {
  const {
    checkData,
    getAfterChangeData,
    getElementXy,
    isMagnetic = false,
    keyboardType,
  } = props;
  const wizardSteps = checkData?.wizard?.steps ?? [];
  const { t } = useTranslation();
  const [currentKeyRef, setCurrentKeyRef] = useState<any>(null);
  const [showTravelModal, setShowTravelModal] = useState(false);
  const [activeDbs, setActiveDbs] = useState<0 | 1 | 2 | 3>(0);

  const setKeyCheck = (key: number, e: any) => {
    const newCheckData = JSON.parse(JSON.stringify(checkData));
    newCheckData.triggerData.forEach((item: any) => {
      item.check = false;
    });
    newCheckData.triggerData[key].check = true;
    getAfterChangeData(newCheckData);
    setCurrentKeyRef(e.currentTarget);
    getElementXy(getElementXyData(e));
  };
  const getClassName = (check: boolean, type: string) => {
    if (check) {
      return `check-item ${type} active`;
    } else {
      return `check-item ${type}`;
    }
  };

  const setChildKeyCheck = (key: number, item: any, keys: number) => {
    const newCheckData = JSON.parse(JSON.stringify(checkData));
    if (!item.check) {
      newCheckData.triggerData[key].binaryData[keys].value = 1;
      if (!isMagnetic && keys === 3) {
        newCheckData.triggerData[key].binaryData[keys + 1].value = 1;
      }
    } else {
      newCheckData.triggerData[key].binaryData[keys].value = 0;
      if (!isMagnetic && keys === 3) {
        newCheckData.triggerData[key].binaryData[keys + 1].value = 0;
      }
    }
    newCheckData.triggerData[key].binaryData[keys].check = !item.check;
    getAfterChangeData(newCheckData);
  };

  useEffect(() => {
    const handleResize = () => {
      if (currentKeyRef) {
        getElementXy(getElementXyData({ target: currentKeyRef }), false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [currentKeyRef, getElementXy]);

  const magneticDbs = normalizeMagneticDbs(
    checkData?.otherData?.dbs,
    keyboardType
  );

  return (
    <div className="advance-setting-box dks-advance-setting-box">
      <div className="advance-setting-box-header">
        <div className="advance-setting-box-header-title">
          {wizardSteps[1]?.titleKey ? t(wizardSteps[1].titleKey) : ""}
        </div>
        <div className="advance-setting-box-header-desc">
          {wizardSteps[1]?.descKey ? t(wizardSteps[1].descKey) : ""}
        </div>
      </div>
      <div className="operation-item operation-dks">
        <div className="dks-mode-box">
          <Tooltip
            className="dks-dynamic-box-wrapper"
            text={
              <div className="dks-dynamic-box-wrapper-tips">
                <span>{t("keyboard.advancedKey.dksModes.tips1.text1")}</span>
                <span>{t("keyboard.advancedKey.dksModes.tips1.text2")}</span>
                <span>{t("keyboard.advancedKey.dksModes.tips1.text3")}</span>
              </div>
            }
            trigger="always"
            placement="right"
            usePortal
            maxWidth={220}
            bgColor="var(--text-subtle)"
            overlayClassName="dks-no-shadow"
          >
            <div
              className={`dks-dynamic-box${isMagnetic ? " dks-dynamic-box--magnetic" : ""}`}
            >
              {checkData?.triggerData?.map((item: any, key: number) => {
                const isActive = !item.label && item.check;
                return (
                  <div className="dks-dynamic-item" key={key}>
                    <div className="pressKey-box">
                      <div
                        className={`pressKey ${isActive ? "no-select" : ""}`}
                        onClick={(e) => setKeyCheck(key, e)}
                      >
                        {item.label ? (
                          <span>{item.label}</span>
                        ) : (
                          <Iconfont icon="plus" className="add-icon" />
                        )}
                      </div>
                    </div>
                    <div className="travel-slider">
                      {(() => {
                        const visibleItems = isMagnetic
                          ? (item?.binaryData?.filter(
                              (b: any) =>
                                Number(b.key) >= 0 && Number(b.key) <= 3
                            ) ?? [])
                          : (item?.binaryData?.filter(
                              (b: any) => b.key !== 4
                            ) ?? []);
                        return visibleItems.map(
                          (items: any, idx: number, arr: any[]) => {
                            // 华奋达磁轴：仍用星闪 tip 悬浮样式，但四点文案/行程各自独立
                            if (isMagnetic) {
                              const tip = MAGNETIC_TIP_META[idx];
                              const nextItem = arr[idx + 1];
                              const connectRight =
                                items.check && nextItem?.check;
                              const displayValue =
                                magneticDbs[tip.dbsIdx] ??
                                HFD_DKS_DEFAULT_DBS[tip.dbsIdx];
                              const formatted = `${Number(displayValue).toFixed(2)}mm`;
                              return (
                                <div
                                  className={[
                                    "check-item-col",
                                    "has-tip",
                                    connectRight ? "connect-right" : "",
                                  ]
                                    .filter(Boolean)
                                    .join(" ")}
                                  key={items.key}
                                >
                                  {key === 0 && tip ? (
                                    <div
                                      className={`dks-tips-list ${tip.tipClass}`}
                                    >
                                      <div className="label">
                                        <div className="icon"></div>
                                        <div className="text">
                                          {t(tip.labelKey)}
                                        </div>
                                      </div>
                                      <div
                                        className="value"
                                        onClick={() => {
                                          setActiveDbs(tip.dbsIdx);
                                          setShowTravelModal(true);
                                        }}
                                      >
                                        {formatted}
                                      </div>
                                    </div>
                                  ) : null}
                                  <div
                                    className={getClassName(
                                      items.check,
                                      items.type
                                    )}
                                    onClick={() =>
                                      setChildKeyCheck(key, items, items.key)
                                    }
                                  >
                                    {items.check ? (
                                      <div className="check-circle"></div>
                                    ) : (
                                      <Iconfont
                                        icon="plus"
                                        className="add-icon"
                                      />
                                    )}
                                  </div>
                                </div>
                              );
                            }

                            const isFirst = idx === 0;
                            const isFourth = idx === 3;
                            const isLast = idx === arr.length - 1;
                            const hasTip =
                              key === 0 && (isFirst || isFourth || isLast);
                            const isTipPos = isFirst || isFourth || isLast;
                            const nextItem = arr[idx + 1];
                            const connectRight = items.check && nextItem?.check;
                            const colClass = [
                              "check-item-col",
                              isTipPos ? "has-tip" : "",
                              connectRight ? "connect-right" : "",
                            ]
                              .filter(Boolean)
                              .join(" ");
                            return (
                              <div className={colClass} key={items.key}>
                                {hasTip && (
                                  <div
                                    className={`dks-tips-list ${
                                      isFirst
                                        ? "press"
                                        : isFourth
                                          ? "bottomOut"
                                          : "reset"
                                    }`}
                                  >
                                    <div className="label">
                                      <div className="icon"></div>
                                      <div className="text">
                                        {isFirst
                                          ? t(
                                              "keyboard.advancedKey.dksModes.start"
                                            )
                                          : isFourth
                                            ? t(
                                                "keyboard.advancedKey.dksModes.bottom"
                                              )
                                            : t(
                                                "keyboard.advancedKey.dksModes.reset"
                                              )}
                                      </div>
                                    </div>
                                    {(() => {
                                      const displayValue = isFirst
                                        ? checkData?.otherData?.dbs?.[0]
                                        : isFourth
                                          ? checkData?.otherData?.dbs?.[1]
                                          : checkData?.otherData?.dbs?.[0];
                                      const formatted =
                                        displayValue != null
                                          ? `${displayValue.toFixed(2)}mm`
                                          : "1.40mm";
                                      const openModal = (dbsIdx: 0 | 1) => {
                                        setActiveDbs(dbsIdx);
                                        setShowTravelModal(true);
                                      };
                                      const dbsIdx: 0 | 1 = isFourth ? 1 : 0;
                                      return isLast ? (
                                        <Tooltip
                                          text={
                                            <div className="pressKey-tooltip">
                                              <Iconfont
                                                icon="warning"
                                                className="pressKey-tooltip-icon"
                                              />
                                              <span>
                                                {t(
                                                  "keyboard.advancedKey.dksModes.tips2"
                                                )}
                                              </span>
                                            </div>
                                          }
                                          trigger="always"
                                          placement="right"
                                          usePortal
                                          gap={40}
                                          bgColor="var(--text-subtle)"
                                          overlayClassName="dks-no-shadow"
                                        >
                                          <div
                                            className="value"
                                            onClick={() => openModal(dbsIdx)}
                                          >
                                            {formatted}
                                          </div>
                                        </Tooltip>
                                      ) : (
                                        <div
                                          className="value"
                                          onClick={() => openModal(dbsIdx)}
                                        >
                                          {formatted}
                                        </div>
                                      );
                                    })()}
                                  </div>
                                )}
                                <div
                                  className={getClassName(
                                    items.check,
                                    items.type
                                  )}
                                  onClick={() =>
                                    setChildKeyCheck(key, items, items.key)
                                  }
                                >
                                  {items.check ? (
                                    <div className="check-circle"></div>
                                  ) : (
                                    <Iconfont
                                      icon="plus"
                                      className="add-icon"
                                    ></Iconfont>
                                  )}
                                </div>
                              </div>
                            );
                          }
                        );
                      })()}
                    </div>
                  </div>
                );
              })}
            </div>
          </Tooltip>
        </div>

        <SettingTravelModal
          showTravelModal={showTravelModal}
          checkData={checkData}
          getAfterChangeData={getAfterChangeData}
          onClose={() => setShowTravelModal(false)}
          activeDbs={activeDbs}
          isMagnetic={isMagnetic}
          keyboardType={keyboardType}
        />
      </div>
    </div>
  );
};
export default dks;
